import { useState, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import Table from 'components/__new/common/Table';
import DateFormat from 'utils/dateFormat';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Filter from 'components/__new/common/Filter';
import { useLocation } from 'react-router-dom';
import { getAllEmployees } from 'store/entities/employees/action';
import { toast } from 'react-toastify';
import processParams from "utils/processParams";
import useUrlQueryParams from 'hooks/useUrlQueryParams';
import Maybe from 'components/__new/common/Maybe';
import Pagination from 'components/__new/common/pagination';
import SearchBox from 'components/__new/common/SearchBox';
import { assignEmployeeToGroup } from 'store/entities/groups/action';
import DynamicHeadTag from 'components/__new/common/DynamicHeadTag';
import { TableContent } from 'components/__new/common/TableContent';

const GroupEmployeesSelection = ({ payload, setCreateFormShown, isLoading, returnToGroups }) => {
    const pageAccess = "all";
    const dispatch = useDispatch()
    const location = useLocation();
    const { urlQueryParams } = useUrlQueryParams();
    const [listParams, setListParams] = useState({
        "page": 1,
        "limit": 20,
    });
    const { employees } = useSelector(state => state.entities.employees)
    const { allGroups: { policies: groups }, assignEmployeeToGroupLoading: assignLoading } = useSelector(state => state.entities.groups)
    const [selected, setSelected] = useState([]);

    const allEmployeeIDs = employees?.employees?.docs?.map(item => item._id)

    const filteredEmployees = employees?.employees?.docs

    const toggleSingleEmployee = (id) => {
        const alreadyExists = selected?.indexOf(id) > -1
        if (alreadyExists) {
            const filtered = selected?.filter(item => item !== id)
            setSelected(filtered)
        } else {
            setSelected([...selected, id])
        }
    }

    const toggleEntireList = () => {
        if (selected.length !== allEmployeeIDs?.length) {
            setSelected(allEmployeeIDs)
        } else {
            setSelected([])
        }
    }

    const onProceed = () => {
        const actual = groups?.find(item => item?.name === payload?.groupName)
        if (!selected.length) {
            toast.error('Select at least one employee')
        } else {
            const payload = {
                policyId: actual?._id,
                employeeIds: selected.join()
            }
            dispatch(assignEmployeeToGroup(payload)).then(() => {
                setSelected([]);
                returnToGroups();
            })
        }
    }


    const getEmployees = () => {
        let params = { ...listParams };

        params = processParams(urlQueryParams, params, "page", "number", 1, "page");
        params = processParams(urlQueryParams, params, "limit", "number", 20, "limit");
        params = processParams(urlQueryParams, params, "search", "string", "", "search");
        params = processParams(urlQueryParams, params, "search", "string", "", "fullName");

        if (pageAccess === "enrolled") {
            params = { ...params, status: "active" };
        }
        else {
            params = processParams(urlQueryParams, params, "status", "string", "", "status");
        }

        setListParams(params);
        dispatch(getAllEmployees(params));
    }

    useEffect(() => {
        getEmployees()
        // eslint-disable-next-line
    }, [location])

    return (
        <div>
            <DynamicHeadTag headerText="Company | Assign Employees to Group" headerDescription="" />
            <div className="flex items-center justify-between">
                <div className=" lg:w-3/4">
                    <div onClick={() => setCreateFormShown(true)} className="page-title flex items-center cursor-pointer w-max">
                        <FontAwesomeIcon icon="angle-left" className='mr-4' />
                        Assign to&nbsp;<span className="capitalize">{payload?.groupName}</span>
                    </div>
                </div>
                <div className="flex lg:mt-0 justify-end space-x-3 lg:w-1/4">
                    <button onClick={onProceed} type="button" className="btn btn-md btn-ep-primary">
                        Done
                    </button>
                </div>
            </div>
            
                <div className="mt-6 bg-white p-3 ">
                    
                    <div className="mt-6 flex flex-wrap justify-between">
                        <div className="max-w-sm flex-grow sm:mr-6">
                            <SearchBox placeholder="Search by name..." name="search" extraUrlParams="page=1" />
                        </div>
                        <div className="flex flex-wrap space-x-3 sm:space-x-6">
                            <Maybe condition={pageAccess === "all"}>
                                <div>
                                    <Filter filters={[
                                        {
                                            title: "Status",
                                            name: "status",
                                            dataType: "radio",
                                            options: [
                                                {
                                                    display: "All",
                                                    payload: "",
                                                },
                                                {
                                                    display: "Enrolled",
                                                    payload: "active",
                                                },
                                                {
                                                    display: "Pending",
                                                    payload: "pending",
                                                },
                                            ],
                                        },
                                    ]} />
                                </div>
                            </Maybe>
                        </div>
                    </div>
                    
                    <div className='mt-8'>
                        <Table>
                            <Table.Head>
                                <th>
                                    <input 
                                        type="checkbox"
                                        onChange={() => toggleEntireList()}
                                        className="w-5 h-5 mr-3.5 form-checkbox float-left"
                                        checked={filteredEmployees?.length > 0 && selected?.length === allEmployeeIDs?.length}
                                    />
                                </th>
                                <th>First Name</th>
                                <th>Last Name</th>
                                <th>Email Address</th>
                                <th>Date Added</th>
                                <th>Job Title</th>
                                <th>Status</th>
                            </Table.Head>
                            <Table.Body>
                                
                                <TableContent
                                    loading={isLoading || assignLoading}
                                    loadingText={assignLoading ? "Submitting" : isLoading ? "Fetching employees" : "Fetching groups"}
                                    data={filteredEmployees}
                                    colspan={8}
                                />
                                
                                {isLoading === false && assignLoading === false && filteredEmployees?.map((employee) => {
                                    const firstName = employee?.fullName.split(" ")[0]
                                    const lastName = employee?.fullName.split(" ")[1]
                                    const empId = employee._id
                                    const isDisabled = employee?.disabled
                                    const isActive = employee.status === 'active'
                                    const isPending = employee.status === 'pending'

                                    return <tr key={empId}>
                                        <td style={{width: "1px"}}>
                                            <input value={empId} onChange={() => toggleSingleEmployee(empId)}
                                                checked={selected?.includes(empId)}
                                                type="checkbox" className="w-5 h-5 mr-2 form-checkbox float-left" />
                                        </td>
                                        <td>
                                            <div className='capitalize'>
                                                {firstName}
                                            </div>
                                        </td>
                                        <td>
                                            <div className='capitalize'>
                                                {lastName}
                                            </div>
                                        </td>
                                        <td>
                                            <div>
                                                {employee.email}
                                            </div>
                                        </td>
                                        <td>
                                            {DateFormat(employee.createdAt)}
                                        </td>
                                        <td>
                                            {employee.jobTitle}
                                        </td>
                                        <td>
                                            <div style={{
                                                color: isDisabled ? '#ff3e30' : isActive ? '#34a853' : isPending ? '#f49d1b' : "",
                                                backgroundColor: isDisabled ? '#EB00001A' : isActive ? '#34A8531A' : isPending ? '#F49D1B1A' : ""
                                            }} className="px-3 py-1 rounded-md flex items-center justify-center capitalize">
                                                {isDisabled ? "Disabled" : employee.status}
                                            </div>
                                        </td>
                                    </tr>
                                }
                                )}

                            </Table.Body>
                        </Table>
                        <Maybe condition={assignLoading === false && isLoading === false && employees?.employees?.docs?.length > 0}>
                            <div className="mt-6">
                                <Pagination data={employees?.employees} />
                            </div>
                        </Maybe>
                    </div>
                </div>
        </div>
    )
}

export default GroupEmployeesSelection
